import { Fragment, useId, useState } from "react";
import type { ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "~/components/base/Button";
import { Checkbox } from "~/components/base/Checkbox";
import { Icon } from "~/components/base/Icon";
import { useT } from "~/hooks/useT";
import { useAuthStore } from "~/stores/authStore";
import type { RegisterPayload } from "~/types";

type Step = 1 | 2 | 3 | "done";

const ESTABLISHMENT_TYPES = [
  "cafe",
  "restaurant",
  "hotel",
  "office",
  "reseller",
] as const;

const CONSUMPTION_RANGES = ["0-20", "20-50", "50-150", "150+"] as const;

interface FormState extends Omit<RegisterPayload, "locations"> {
  locations: string; // kept as string in form for free-form numeric input
}

const initialForm: FormState = {
  legalName: "",
  edrpou: "",
  vat: "yes",
  brand: "",
  establishmentType: "cafe",
  locations: "1",
  monthlyConsumption: "20-50",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  comment: "",
  acceptTos: false,
};

/**
 * Register screen — ported from the React reference `components/register.jsx`.
 *
 * Three-step wizard:
 *   1. Company    — legalName, edrpou, vat, brand
 *   2. Establishment — type, locations, monthlyConsumption
 *   3. Contacts   — name, email, phone, comment, ToS
 *
 * On final submit, calls `useAuthStore.register()` with a normalised
 * `RegisterPayload` (locations coerced from string to number). The success
 * screen explains the three follow-up promises (manager outreach, verification,
 * −5% first-order discount) and routes back to `/login`.
 */
export function Register() {
  const { t } = useT();
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);

  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [stepErr, setStepErr] = useState("");

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const goNext = () => {
    setStepErr("");
    if (step === 1) {
      if (!form.legalName.trim() || !form.edrpou.trim()) {
        setStepErr(t("r_required_field"));
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      setStep(3);
    }
  };

  const goBack = () => {
    setStepErr("");
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const submit = async () => {
    setStepErr("");
    if (!form.contactEmail.includes("@")) {
      setStepErr(t("invalid_email"));
      return;
    }
    if (!form.acceptTos) {
      setStepErr(t("r_accept_tos_required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload: RegisterPayload = {
        ...form,
        locations: Number.parseInt(form.locations, 10) || 1,
      };
      await register(payload);
      setStep("done");
    } catch {
      setStepErr(t("r_submit_error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "done") {
    return (
      <div style={{ maxWidth: 440, width: "100%", margin: "auto 0" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            background: "var(--ok-bg)",
            color: "var(--ok)",
            display: "grid",
            placeItems: "center",
            marginBottom: 20,
          }}
        >
          <Icon name="check" size={26} />
        </div>
        <h1 className="h1" style={{ fontSize: 44, marginBottom: 12 }}>
          {t("r_sent_title")}
        </h1>
        <p
          style={{
            color: "var(--ink-3)",
            fontSize: 14,
            lineHeight: 1.55,
            marginBottom: 28,
          }}
        >
          {t("r_sent_sub")}{" "}
          <strong style={{ color: "var(--ink)" }}>
            {form.contactPhone || "+380 __ ___ __ __"}
          </strong>
          .
        </p>

        <div
          className="mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.08em",
            color: "var(--ink-4)",
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          {t("r_what_next")}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 32,
          }}
        >
          {(["r_n1", "r_n2", "r_n3"] as const).map((key, i) => (
            <div
              key={key}
              style={{
                display: "flex",
                gap: 12,
                padding: 12,
                background: "var(--surface)",
                border: "1px solid var(--line)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: "var(--ink)",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{t(key)}</div>
            </div>
          ))}
        </div>

        <Button variant="secondary" onClick={() => navigate("/login")}>
          {t("r_back_to_site")}
        </Button>
      </div>
    );
  }

  const numericStep: 1 | 2 | 3 = step;

  return (
    <div style={{ margin: "auto 0", maxWidth: 480, width: "100%" }}>
      <div className="eyebrow" style={{ marginBottom: 14 }}>
        {t("r_eyebrow")}
      </div>
      <h1 className="h1" style={{ fontSize: 40, marginBottom: 12 }}>
        {t("r_title")}
      </h1>
      <p
        style={{
          color: "var(--ink-3)",
          fontSize: 14,
          marginBottom: 28,
          lineHeight: 1.5,
        }}
      >
        {t("r_sub")}
      </p>

      <StepDots step={numericStep} t={t} />

      {numericStep === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field
            label={t("r_company_legal")}
            hint={t("r_company_hint")}
            value={form.legalName}
            onChange={(v) => update("legalName", v)}
            placeholder="ТОВ «Кавовий Дім»"
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 12,
            }}
          >
            <Field
              label={t("r_edrpou")}
              value={form.edrpou}
              onChange={(v) => update("edrpou", v)}
              placeholder="42318976"
            />
            <div>
              <div className="label">{t("r_vat")}</div>
              <Seg2
                value={form.vat}
                onChange={(v) => update("vat", v)}
                options={[
                  { v: "yes", l: t("r_yes") },
                  { v: "no", l: t("r_no") },
                ]}
              />
            </div>
          </div>
          <Field
            label={t("r_brand_name")}
            hint={t("r_brand_hint")}
            value={form.brand}
            onChange={(v) => update("brand", v)}
            placeholder="Brew & Bros Café"
          />
        </div>
      )}

      {numericStep === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div className="label">{t("r_venue_type")}</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 6,
              }}
            >
              {ESTABLISHMENT_TYPES.map((k) => {
                const active = form.establishmentType === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => update("establishmentType", k)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 6,
                      fontSize: 12.5,
                      border: `1px solid ${
                        active ? "var(--ink)" : "var(--line-2)"
                      }`,
                      background: active ? "var(--ink)" : "var(--surface)",
                      color: active ? "#fff" : "var(--ink-2)",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    {t(`est_${k}`)}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: 12,
            }}
          >
            <Field
              label={t("r_venues")}
              value={form.locations}
              onChange={(v) =>
                update("locations", v.replace(/[^0-9]/g, ""))
              }
              placeholder="1"
              inputMode="numeric"
            />
            <div>
              <label className="label" htmlFor="reg-volume">
                {t("r_volume")}
              </label>
              <select
                id="reg-volume"
                className="select"
                value={form.monthlyConsumption}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  update("monthlyConsumption", e.target.value)
                }
              >
                {CONSUMPTION_RANGES.map((r, idx) => (
                  <option key={r} value={r}>
                    {t(`r_v${idx + 1}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {numericStep === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field
            label={t("r_name_full")}
            value={form.contactName}
            onChange={(v) => update("contactName", v)}
            placeholder="Олег Шевченко"
          />
          <Field
            label={t("r_email")}
            value={form.contactEmail}
            onChange={(v) => update("contactEmail", v)}
            placeholder="oleg@brewbros.ua"
            type="email"
          />
          <Field
            label={t("r_phone")}
            value={form.contactPhone}
            onChange={(v) => update("contactPhone", v)}
            placeholder="+380 50 483 29 11"
          />
          <div>
            <label className="label" htmlFor="reg-note">
              {t("r_note")}
            </label>
            <textarea
              id="reg-note"
              className="input"
              style={{ height: 80, padding: 10, resize: "vertical" }}
              placeholder={t("r_note_ph")}
              value={form.comment ?? ""}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                update("comment", e.target.value)
              }
            />
          </div>
          <Checkbox
            checked={form.acceptTos}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update("acceptTos", e.target.checked)
            }
            label={t("r_accept_tos")}
          />
        </div>
      )}

      {stepErr && (
        <div className="help help--err" style={{ marginTop: 10 }}>
          {stepErr}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
        {numericStep > 1 && (
          <Button
            variant="secondary"
            onClick={goBack}
            leftIcon={<Icon name="chevron-left" size={13} />}
          >
            {t("r_back")}
          </Button>
        )}
        <div style={{ flex: 1 }} />
        {numericStep < 3 ? (
          <Button
            variant="blue"
            onClick={goNext}
            rightIcon={<Icon name="chevron-right" size={13} />}
          >
            {t("r_continue")}
          </Button>
        ) : (
          <Button
            variant="blue"
            size="lg"
            onClick={() => {
              void submit();
            }}
            disabled={submitting}
          >
            {submitting ? t("signing_in") : t("r_submit")}
          </Button>
        )}
      </div>

      <div style={{ marginTop: 28, fontSize: 13, color: "var(--ink-3)" }}>
        {t("r_have_account")}{" "}
        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{
            color: "var(--ink)",
            textDecoration: "underline",
            textUnderlineOffset: 2,
            fontWeight: 500,
            background: "none",
            border: 0,
            padding: 0,
            cursor: "pointer",
            font: "inherit",
          }}
        >
          {t("r_sign_in")}
        </button>
      </div>
    </div>
  );
}

interface StepDotsProps {
  step: 1 | 2 | 3;
  t: (k: string) => string;
}

function StepDots({ step, t }: StepDotsProps) {
  const total = 3;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 28,
      }}
    >
      {[1, 2, 3].map((i) => (
        <Fragment key={i}>
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: 999,
              background:
                i < step
                  ? "var(--ink)"
                  : i === step
                    ? "var(--surface)"
                    : "var(--surface-3)",
              color:
                i < step
                  ? "#fff"
                  : i === step
                    ? "var(--ink)"
                    : "var(--ink-4)",
              border:
                i === step
                  ? "2px solid var(--ink)"
                  : "1px solid var(--line-2)",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {i < step ? <Icon name="check" size={11} /> : i}
          </div>
          {i < 3 && (
            <div
              style={{
                height: 1,
                flex: 1,
                background: i < step ? "var(--ink)" : "var(--line-2)",
              }}
            />
          )}
        </Fragment>
      ))}
      <span
        className="mono"
        style={{
          fontSize: 10.5,
          letterSpacing: "0.06em",
          color: "var(--ink-4)",
          textTransform: "uppercase",
          marginLeft: 4,
        }}
      >
        {t("r_step")} {step} {t("r_of")} {total}
      </span>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  placeholder?: string;
  type?: string;
  inputMode?: "text" | "numeric" | "email" | "tel" | "url" | "search";
}

function Field({
  label,
  value,
  onChange,
  hint,
  placeholder,
  type = "text",
  inputMode,
}: FieldProps) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="label">{label}</label>
      <input
        id={id}
        className="input"
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <div className="help">{hint}</div>}
    </div>
  );
}

interface Seg2Option {
  v: string;
  l: string;
}

interface Seg2Props {
  options: Seg2Option[];
  value: string;
  onChange: (v: string) => void;
}

function Seg2({ options, value, onChange }: Seg2Props) {
  return (
    <div
      style={{
        display: "inline-flex",
        width: "100%",
        background: "var(--surface-3)",
        borderRadius: 6,
        padding: 2,
      }}
    >
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            style={{
              flex: 1,
              height: 32,
              borderRadius: 4,
              fontSize: 12.5,
              background: active ? "var(--surface)" : "transparent",
              color: active ? "var(--ink)" : "var(--ink-3)",
              fontWeight: active ? 600 : 500,
              boxShadow: active ? "var(--shadow-xs)" : "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

export default Register;
